import { createContext, useState } from 'react'
import { useUser } from 'src/hook/useUser'
import { useCommunityDetails } from 'src/pages/CommunityDetails/Posts/context/communityDetailsContext'
import {
  PUBLIC_COMMUNITY_MEMBER_ASSOCIATION_COMPONENT_SOURCE,
  PUBLIC_COMMUNITY_MEMBER_ASSOCIATION_SCREEN_NAME,
  PUBLIC_COMMUNITY_MEMBER_ASSOCIATION,
  PUBLIC_COMMUNITY_STORED_COMMENT
} from 'src/utils/constants'
import {
  Actions,
  ComponentSources,
  Entities,
  EventNames,
  ScreenNames,
  ResultsTypes
} from 'src/utils/datahub/enums'
import { AuthStatus } from '@hotmart/space-lib/dist/enum'
import { toastEventDispatch } from '@hotmart/space-lib-events/dist/toast'
import { ToastPosition, ToastType } from '@hotmart/space-lib-events/dist/toast/enum'

import { loginEventDispatch } from '@hotmart/space-lib-events/dist/login'
import i18n from 'src/i18n'
import useSendDataHubConsume from 'src/hook/useSendDatahubConsume/useSendDatahubConsume'
import { queryClient } from 'src/config/request'
import { CommunitiesEndpoints } from 'src/client/constants'
import { usePublicCommunityMutation } from 'src/hook/usePublicCommunityMutation'
import { PublicCommunityContextProps, PublicCommunityProps } from './publicCommunityContext.types'

export const PublicCommunityContext = createContext<PublicCommunityContextProps>(
  {} as PublicCommunityContextProps
)

export const PublicCommunityProvider = ({ children }: PublicCommunityProps) => {
  const { authStatus } = useUser()
  const { community } = useCommunityDetails()
  const { sendDataHubConsume } = useSendDataHubConsume()
  const [storedComment, setStoredComment] = useState('')
  const [commentToStore, setCommentToStore] = useState('')
  const [shouldStoreComment, setShouldStoreComment] = useState(false)
  const [isLoadingPublicCommunity, setIsLoadingPublicCommunity] = useState(false)
  const [isPublicCommunityModalOpen, setIsPublicCommunityModalOpen] = useState(false)
  const {
    putFollowPublicCommunityMutation: { mutate: putFollowPublicCommunityMutate },
    putUnfollowPublicCommunityMutation: { mutate: putUnfollowPublicCommunityMutate }
  } = usePublicCommunityMutation()
  const [screenName, setScreenName] = useState(
    sessionStorage.getItem(PUBLIC_COMMUNITY_MEMBER_ASSOCIATION_SCREEN_NAME) as ScreenNames
  )
  const [componentSource, setComponentSource] = useState(
    sessionStorage.getItem(PUBLIC_COMMUNITY_MEMBER_ASSOCIATION_COMPONENT_SOURCE) as ComponentSources
  )

  const handleSuccessCommunityFollow = (onRefetchCommunityQuery: () => void) => {
    onRefetchCommunityQuery()

    setIsPublicCommunityModalOpen(false)

    setIsLoadingPublicCommunity(false)

    toastEventDispatch({
      type: ToastType.SUCCESS,
      message: i18n.t('public_community.follow.success'),
      position: ToastPosition.BOTTOM_RIGHT
    })

    sendDataHubConsume({
      action: Actions.FOLLOW,
      entity: Entities.COMMUNITY,
      event: {
        eventName: EventNames.TRY_TO_FOLLOW_COMMUNITY,
        screenName,
        componentSource,
        result: ResultsTypes.SUCCESS,
        isLogged: true
      }
    })
  }

  const handleErrorCommunityFollow = (error) => {
    setIsLoadingPublicCommunity(false)
    setIsPublicCommunityModalOpen(true)

    toastEventDispatch({
      type: ToastType.DANGER,
      message: i18n.t('public_community.follow.error'),
      position: ToastPosition.BOTTOM_RIGHT
    })

    sendDataHubConsume({
      action: Actions.FOLLOW,
      entity: Entities.COMMUNITY,
      event: {
        eventName: EventNames.TRY_TO_FOLLOW_COMMUNITY,
        screenName,
        componentSource,
        result: ResultsTypes.FAILURE,
        isLogged: true,
        failureKind: error.message
      }
    })
  }

  const handlePublicCommunityFollow = (onRefetchCommunityQuery: () => void) => {
    if (shouldStoreComment) {
      sessionStorage.setItem(PUBLIC_COMMUNITY_STORED_COMMENT, commentToStore)
    }

    if (authStatus === AuthStatus.AUTHENTICATED) {
      setIsLoadingPublicCommunity(true)
      putFollowPublicCommunityMutate(
        {
          communityId: community?.id
        },
        {
          onSuccess: /* istanbul ignore next */ () => {
            handleSuccessCommunityFollow(onRefetchCommunityQuery)
          },
          onError: handleErrorCommunityFollow
        }
      )

      return
    }

    sessionStorage.setItem(PUBLIC_COMMUNITY_MEMBER_ASSOCIATION, 'true')
    setIsPublicCommunityModalOpen(false)
    loginEventDispatch({ signInRedirect: true })
  }

  const handleSuccessCommunityUnfollow = () => {
    queryClient.invalidateQueries({
      queryKey: [CommunitiesEndpoints.getCommunity(community?.id)]
    })

    setIsLoadingPublicCommunity(false)

    toastEventDispatch({
      type: ToastType.SUCCESS,
      message: i18n.t('public_community.unfollow.success'),
      position: ToastPosition.BOTTOM_RIGHT
    })

    sendDataHubConsume({
      action: Actions.FOLLOW,
      entity: Entities.COMMUNITY,
      event: {
        eventName: EventNames.TRY_TO_UNFOLLOW_COMMUNITY,
        screenName: ScreenNames.COMMUNITY_FEED,
        componentSource: ComponentSources.COMMUNITY_HEADER,
        result: ResultsTypes.SUCCESS,
        isLogged: true
      }
    })
  }

  const handleErrorCommunityUnfollow = (error) => {
    setIsLoadingPublicCommunity(false)

    toastEventDispatch({
      type: ToastType.DANGER,
      message: i18n.t('public_community.unfollow.error'),
      position: ToastPosition.BOTTOM_RIGHT
    })

    sendDataHubConsume({
      action: Actions.FOLLOW,
      entity: Entities.COMMUNITY,
      event: {
        eventName: EventNames.TRY_TO_UNFOLLOW_COMMUNITY,
        screenName: ScreenNames.COMMUNITY_FEED,
        componentSource: ComponentSources.COMMUNITY_HEADER,
        result: ResultsTypes.FAILURE,
        isLogged: true,
        failureKind: error.message
      }
    })
  }

  const handlePublicCommunityUnfollow = () => {
    setIsLoadingPublicCommunity(true)

    putUnfollowPublicCommunityMutate(
      {
        communityId: community?.id
      },
      {
        onSuccess: handleSuccessCommunityUnfollow,
        onError: handleErrorCommunityUnfollow
      }
    )
  }

  return (
    <PublicCommunityContext.Provider
      value={{
        screenName,
        setScreenName,
        storedComment,
        componentSource,
        setStoredComment,
        setCommentToStore,
        setComponentSource,
        setShouldStoreComment,
        isLoadingPublicCommunity,
        handleErrorCommunityFollow,
        isPublicCommunityModalOpen,
        handlePublicCommunityFollow,
        setIsLoadingPublicCommunity,
        handleErrorCommunityUnfollow,
        handleSuccessCommunityFollow,
        handlePublicCommunityUnfollow,
        setIsPublicCommunityModalOpen,
        handleSuccessCommunityUnfollow
      }}
    >
      {children}
    </PublicCommunityContext.Provider>
  )
}
